const NEW_YORK_TIME_ZONE = 'America/New_York';
const DEFAULT_TIME_ZONE = NEW_YORK_TIME_ZONE;
const ETC_GMT_TIME_ZONE = 'Etc/GMT';
const KOLKATA_TIME_ZONE = 'Asia/Kolkata';

const exactTimeZoneFallbacks: Record<string, string> = {
  GMT: ETC_GMT_TIME_ZONE,
  GMT0: ETC_GMT_TIME_ZONE,
  'GMT+0': ETC_GMT_TIME_ZONE,
  'GMT-0': ETC_GMT_TIME_ZONE,
  UTC: ETC_GMT_TIME_ZONE,
  'UTC+0': ETC_GMT_TIME_ZONE,
  'UTC-0': ETC_GMT_TIME_ZONE,

  // Misused placeholders
  Local: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const legacyAbbreviationFallbacks: Record<string, string> = {
  EST: NEW_YORK_TIME_ZONE,
  EDT: NEW_YORK_TIME_ZONE,
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  IST: KOLKATA_TIME_ZONE, // Could also be Israel
  BST: 'Europe/London',
  HST: 'Pacific/Honolulu',
};

// Intl.DateTimeFormat expects IANA zones or supported offset IDs, while Akamai EdgeScape can
// send legacy/offset-ish values like EST+1 or GMT+5.50.
// MDN: Intl.DateTimeFormat() constructor, timeZone option.
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
//
// Fractional Etc/GMT zones do not exist, so use representative IANA zones for common offsets.
const fractionalOffsetFallbacks: Record<string, string> = {
  '+3:30': 'Asia/Tehran',
  '+4:30': 'Asia/Kabul',
  '+5:30': KOLKATA_TIME_ZONE,
  '+5:45': 'Asia/Kathmandu',
  '+6:30': 'Asia/Yangon',
  '+8:45': 'Australia/Eucla',
  '+9:30': 'Australia/Adelaide',
  '+10:30': 'Australia/Lord_Howe',
  '+12:45': 'Pacific/Chatham',
  '-3:30': 'America/St_Johns',
  '-9:30': 'Pacific/Marquesas',
};

export const safeTimeZone = (tz?: string): string => {
  const normalizedTimeZone = tz?.trim();

  if (!normalizedTimeZone) {
    return DEFAULT_TIME_ZONE;
  }

  const mappedTimeZone =
    exactTimeZoneFallbacks[normalizedTimeZone] ??
    normalizeGmtOrUtcOffset(normalizedTimeZone) ??
    normalizeLegacyAbbreviation(normalizedTimeZone);

  if (mappedTimeZone) {
    return isValidTimeZone(mappedTimeZone) ? mappedTimeZone : DEFAULT_TIME_ZONE;
  }

  return isValidTimeZone(normalizedTimeZone) ? normalizedTimeZone : DEFAULT_TIME_ZONE;
};

const normalizeGmtOrUtcOffset = (timeZone: string): string | undefined => {
  const match = timeZone.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?:(:|\.)(\d{1,2}))?$/);

  if (!match) {
    return;
  }

  const [, sign, hoursValue, separator, minutesValue] = match;
  const hours = Number(hoursValue);
  const minutes = normalizeOffsetMinutes(separator, minutesValue);

  if (hours > 14 || minutes === undefined) {
    return;
  }

  if (minutes === 0) {
    // IANA Etc/GMT signs intentionally follow POSIX, so the sign is reversed here:
    // GMT+5 -> Etc/GMT-5. See the tzdb etcetera notes.
    // https://raw.githubusercontent.com/eggert/tz/main/etcetera
    return hours === 0
      ? ETC_GMT_TIME_ZONE
      : `${ETC_GMT_TIME_ZONE}${sign === '+' ? '-' : '+'}${hours}`;
  }

  return fractionalOffsetFallbacks[`${sign}${hours}:${minutes.toString().padStart(2, '0')}`];
};

const normalizeLegacyAbbreviation = (timeZone: string): string | undefined => {
  const abbreviation = timeZone.match(/^([A-Z]{2,4})(?:[+-]\d{1,2}(?:(?::|\.)\d{1,2})?)?$/)?.[1];
  return abbreviation ? legacyAbbreviationFallbacks[abbreviation] : undefined;
};

const normalizeOffsetMinutes = (separator?: string, minutesValue?: string): number | undefined => {
  if (!minutesValue) {
    return 0;
  }

  if (separator === ':') {
    const minutes = Number(minutesValue);
    return minutes >= 0 && minutes < 60 ? minutes : undefined;
  }

  const decimalFraction = Number(`0.${minutesValue}`);
  const minutes = decimalFraction * 60;
  return Number.isInteger(minutes) && minutes >= 0 && minutes < 60 ? minutes : undefined;
};

const isValidTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};
